/**
 * PhoneCall
 *
 * Outgoing (default): menu → contacts → ringing → connected dialogue.
 * Incoming: ringing ("Appel entrant") → optional auto-answer → connected dialogue + in-call choices.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { PhoneCallMessage, PhoneCallReplyOption, Scene } from '../types';
import {
  extractPhoneBranchContinuations,
  PhoneBranchContinuation,
  resolvePhoneReplyGotoScene,
} from '../systems/phone-branch';
import styles from './phone-call.module.css';
import { isTypingInEditableField } from '../systems/keyboard-utils';

type CallPhase = 'menu' | 'contacts' | 'calling' | 'connected' | 'ended';
type CallAnimPhase = 'phase-transition' | 'message-wait' | 'ending-wait' | 'ended-wait' | 'idle';

interface PhoneCallProps {
  contactName: string;
  contactAvatar?: string;
  messages: PhoneCallMessage[];
  messageDelay?: number;
  incomingCall?: boolean;
  autoAnswer?: boolean;
  allowDecline?: boolean;
  ringDurationMs?: number;
  ringAfterDeclineMs?: number;
  replyOptions?: PhoneCallReplyOption[];
  /** When true, skip the standalone phone app menu (scenario-driven call). */
  embeddedInScenario?: boolean;
  /** Resolve branch scene content for reply options with gotoScene (inline editor branches). */
  getBranchScene?: (sceneId: string) => Scene | undefined;
  /** Fallback convergence scene when a leaf reply has contact text but no gotoScene. */
  defaultConvergeSceneId?: string;
  onComplete?: (gotoScene?: string) => void;
}

interface DisplayedLine {
  message: PhoneCallMessage;
  isComplete: boolean;
}

export const PhoneCall = ({
  contactName,
  contactAvatar,
  messages,
  messageDelay = 3000,
  incomingCall = false,
  autoAnswer = false,
  allowDecline = false,
  ringDurationMs = 2200,
  ringAfterDeclineMs: _ringAfterDeclineMs,
  replyOptions,
  embeddedInScenario = false,
  getBranchScene,
  defaultConvergeSceneId,
  onComplete,
}: PhoneCallProps) => {
  const [phase, setPhase] = useState<CallPhase>(() => {
    if (embeddedInScenario) {
      return incomingCall ? 'calling' : 'connected';
    }
    return incomingCall ? 'calling' : 'menu';
  });
  const [declinedOnce, setDeclinedOnce] = useState(false);
  const [displayedLines, setDisplayedLines] = useState<DisplayedLine[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [callDuration, setCallDuration] = useState(0);
  const [pendingReplyOptions, setPendingReplyOptions] = useState<PhoneCallReplyOption[] | null>(null);
  const [awaitingHangup, setAwaitingHangup] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingGotoSceneRef = useRef<string | undefined>();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const animPhaseRef = useRef<CallAnimPhase>('idle');
  const phaseRef = useRef<CallPhase>(phase);
  const currentIndexRef = useRef(0);
  const lastSkipTimeRef = useRef(0);
  const onCompleteRef = useRef(onComplete);
  const replyOptionsRef = useRef(replyOptions);
  const getBranchSceneRef = useRef(getBranchScene);
  const defaultConvergeSceneIdRef = useRef(defaultConvergeSceneId);
  const playBranchContinuationsRef = useRef<
    (lines: PhoneBranchContinuation[], index: number, onDone: (gotoScene?: string) => void) => void
  >(() => {});
  /** True after the player picks any in-call reply (prevents re-showing root choices). */
  const inReplyFlowRef = useRef(false);
  const rootRepliesOfferedRef = useRef(false);
  onCompleteRef.current = onComplete;
  replyOptionsRef.current = replyOptions;
  getBranchSceneRef.current = getBranchScene;
  defaultConvergeSceneIdRef.current = defaultConvergeSceneId;

  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  playBranchContinuationsRef.current = (lines, index, onDone) => {
    if (index >= lines.length) {
      onDone(lines.length > 0 ? lines[lines.length - 1]?.gotoScene : undefined);
      return;
    }
    const line = lines[index]!;
    setDisplayedLines((prev) => [
      ...prev,
      { message: { sender: 'player', text: line.playerText }, isComplete: true },
    ]);
    scrollToBottom();

    const advance = () => {
      if (index + 1 < lines.length) {
        playBranchContinuationsRef.current(lines, index + 1, onDone);
      } else {
        onDone(line.gotoScene);
      }
    };

    if (line.contactReply) {
      timeoutRef.current = setTimeout(() => {
        setDisplayedLines((prev) => [
          ...prev,
          { message: { sender: 'contact', text: line.contactReply! }, isComplete: true },
        ]);
        scrollToBottom();
        advance();
      }, messageDelay * 0.65);
    } else {
      advance();
    }
  };

  const handleAnswerCall = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      animPhaseRef.current = 'idle';
      setPhase('connected');
    },
    []
  );

  const handleDeclineCall = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!allowDecline || declinedOnce) return;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setDeclinedOnce(true);
      animPhaseRef.current = 'idle';
      setPhase('calling');
    },
    [allowDecline, declinedOnce]
  );

  const finishCall = useCallback((gotoScene?: string) => {
    setAwaitingHangup(false);
    if (gotoScene) {
      onCompleteRef.current?.(gotoScene);
      return;
    }
    animPhaseRef.current = 'ending-wait';
    timeoutRef.current = setTimeout(() => {
      setPhase('ended');
      if (intervalRef.current) clearInterval(intervalRef.current);
      animPhaseRef.current = 'ended-wait';
      timeoutRef.current = setTimeout(() => {
        animPhaseRef.current = 'idle';
        onCompleteRef.current?.();
      }, 1500);
    }, 1200);
  }, []);

  const enterAwaitingHangup = useCallback((gotoScene?: string) => {
    pendingGotoSceneRef.current = gotoScene;
    setAwaitingHangup(true);
    scrollToBottom();
  }, [scrollToBottom]);

  const handleHangupClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      finishCall(pendingGotoSceneRef.current);
    },
    [finishCall]
  );

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const handleReplyOptionClick = useCallback(
    (option: PhoneCallReplyOption) => {
      inReplyFlowRef.current = true;
      setPendingReplyOptions(null);
      setDisplayedLines(prev => [
        ...prev,
        { message: { sender: 'player', text: option.text }, isComplete: true },
      ]);
      scrollToBottom();

      const afterContact = () => {
        if (option.replyOptions?.length) {
          setPendingReplyOptions(option.replyOptions);
          scrollToBottom();
        } else {
          const gotoScene = resolvePhoneReplyGotoScene(
            option,
            defaultConvergeSceneIdRef.current
          );
          if (gotoScene) {
            const branchLines = extractPhoneBranchContinuations(
              getBranchSceneRef.current?.(gotoScene)
            );
            if (branchLines.length > 0) {
              playBranchContinuationsRef.current(branchLines, 0, (goto) =>
                enterAwaitingHangup(goto)
              );
            } else {
              enterAwaitingHangup(gotoScene);
            }
          } else {
            enterAwaitingHangup(undefined);
          }
        }
      };

      if (option.contactReply) {
        timeoutRef.current = setTimeout(() => {
          setDisplayedLines(prev => [
            ...prev,
            { message: { sender: 'contact', text: option.contactReply! }, isComplete: true },
          ]);
          scrollToBottom();
          afterContact();
        }, messageDelay * 0.65);
      } else {
        afterContact();
      }
    },
    [messageDelay, scrollToBottom, enterAwaitingHangup]
  );

  const skipToNext = useCallback(() => {
    if (pendingReplyOptions || awaitingHangup) return;
    const now = Date.now();
    if (now - lastSkipTimeRef.current < 150) return;
    lastSkipTimeRef.current = now;

    const sub = animPhaseRef.current;

    if (sub === 'phase-transition') {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      animPhaseRef.current = 'idle';
      const p = phaseRef.current;
      if (p === 'menu') setPhase(incomingCall ? 'calling' : 'contacts');
      else if (p === 'contacts') setPhase('calling');
      else if (p === 'calling' && !(incomingCall && allowDecline)) setPhase('connected');
    } else if (sub === 'message-wait') {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      const idx = currentIndexRef.current;
      const msg = messages[idx];
      if (msg) {
        setDisplayedLines(prev => [...prev, { message: msg, isComplete: true }]);
        scrollToBottom();
      }
      animPhaseRef.current = 'idle';
      setCurrentIndex(i => i + 1);
    } else if (sub === 'ending-wait' || sub === 'ended-wait') {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
      animPhaseRef.current = 'idle';
      onCompleteRef.current?.();
    }
  }, [messages, scrollToBottom, pendingReplyOptions, awaitingHangup, incomingCall, allowDecline]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === ' ') {
        if (isTypingInEditableField()) return;
        e.preventDefault();
        skipToNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [skipToNext]);

  // Outgoing standalone app flow: menu -> contacts -> calling
  useEffect(() => {
    if (incomingCall || embeddedInScenario) return;
    if (phase === 'menu') {
      animPhaseRef.current = 'phase-transition';
      timeoutRef.current = setTimeout(() => { animPhaseRef.current = 'idle'; setPhase('contacts'); }, 1800);
    } else if (phase === 'contacts') {
      animPhaseRef.current = 'phase-transition';
      timeoutRef.current = setTimeout(() => { animPhaseRef.current = 'idle'; setPhase('calling'); }, 1800);
    } else if (phase === 'calling') {
      animPhaseRef.current = 'phase-transition';
      timeoutRef.current = setTimeout(() => { animPhaseRef.current = 'idle'; setPhase('connected'); }, 2500);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [phase, incomingCall, embeddedInScenario]);

  // Incoming: auto-answer after ring (when decline is not allowed)
  useEffect(() => {
    if (!incomingCall || !autoAnswer || allowDecline || phase !== 'calling') return;
    animPhaseRef.current = 'phase-transition';
    timeoutRef.current = setTimeout(() => {
      animPhaseRef.current = 'idle';
      setPhase('connected');
    }, ringDurationMs);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [incomingCall, autoAnswer, allowDecline, phase, ringDurationMs]);

  useEffect(() => {
    if (phase === 'connected') {
      intervalRef.current = setInterval(() => {
        setCallDuration(d => d + 1);
      }, 1000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [phase]);

  useEffect(() => {
    if (phase !== 'connected') return;
    if (pendingReplyOptions) return;

    if (currentIndex >= messages.length) {
      if (inReplyFlowRef.current || awaitingHangup) {
        return;
      }
      if (!rootRepliesOfferedRef.current) {
        const options = replyOptionsRef.current;
        if (options?.length) {
          rootRepliesOfferedRef.current = true;
          setPendingReplyOptions(options);
          scrollToBottom();
          return;
        }
      }
      enterAwaitingHangup();
      return;
    }

    const msg = messages[currentIndex];
    if (!msg) return;

    const delay = currentIndex === 0 ? 800 : messageDelay;

    animPhaseRef.current = 'message-wait';
    timeoutRef.current = setTimeout(() => {
      animPhaseRef.current = 'idle';
      setDisplayedLines(prev => [...prev, { message: msg, isComplete: true }]);
      scrollToBottom();
      setCurrentIndex(i => i + 1);
    }, delay);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [phase, currentIndex, messages, messageDelay, pendingReplyOptions, awaitingHangup, enterAwaitingHangup, scrollToBottom]);

  useEffect(() => {
    scrollToBottom();
  }, [displayedLines, pendingReplyOptions, scrollToBottom]);

  const contactInitial = contactName.charAt(0).toUpperCase();

  if (phase === 'menu') {
    return (
      <div className={styles.callContainer} onClick={skipToNext}>
        <div className={styles.menuScreen}>
          <div className={styles.menuGrid}>
            <div className={styles.menuItem}>
              <div className={styles.menuIcon}>
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>
              </div>
              <span className={styles.menuLabel}>Messages</span>
            </div>
            <div className={`${styles.menuItem} ${styles.menuItemHighlighted}`}>
              <div className={`${styles.menuIcon} ${styles.menuIconCall}`}>
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
              </div>
              <span className={styles.menuLabel}>Appel</span>
            </div>
            <div className={styles.menuItem}>
              <div className={styles.menuIcon}>
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
              </div>
              <span className={styles.menuLabel}>Contacts</span>
            </div>
            <div className={styles.menuItem}>
              <div className={styles.menuIcon}>
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/></svg>
              </div>
              <span className={styles.menuLabel}>Alertes</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'contacts') {
    return (
      <div className={styles.callContainer} onClick={skipToNext}>
        <div className={styles.contactsScreen}>
          <div className={styles.contactsHeader}>
            <span>Contacts</span>
          </div>
          <div className={styles.contactsList}>
            <div className={`${styles.contactItem} ${styles.contactItemHighlighted}`}>
              <div className={styles.contactItemAvatarHighlighted}>
                {contactAvatar ? (
                  <img src={contactAvatar} alt={contactName} />
                ) : (
                  contactInitial
                )}
              </div>
              <span>{contactName}</span>
              <div className={styles.callButton}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'calling') {
    return (
      <div className={styles.callContainer} onClick={skipToNext}>
        <div className={styles.callingScreen}>
          <div className={styles.callingAvatar}>
            {contactAvatar ? (
              <img src={contactAvatar} alt={contactName} />
            ) : (
              contactInitial
            )}
          </div>
          <div className={styles.callingName}>{contactName}</div>
          <div className={styles.callingStatus}>
            {incomingCall
              ? declinedOnce
                ? 'Appel entrant… (insistant)'
                : 'Appel entrant…'
              : 'Appel en cours…'}
          </div>
          <div className={styles.callingPulse} />
          <div className={`${styles.callActions} ${styles.incomingCallActions}`}>
            {incomingCall && (
              <>
                {allowDecline && !declinedOnce ? (
                  <button
                    type="button"
                    className={`${styles.callActionBtn} ${styles.declineBtn}`}
                    onClick={handleDeclineCall}
                    aria-label="Ignorer"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.1-.7-.28-.79-.73-1.68-1.36-2.66-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/></svg>
                  </button>
                ) : (
                  <div className={`${styles.callActionBtn} ${styles.declineBtn} ${styles.declineBtnHidden}`} aria-hidden />
                )}
                <button
                  type="button"
                  className={`${styles.callActionBtn} ${styles.answerBtn}`}
                  onClick={handleAnswerCall}
                  aria-label="Décrocher"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
                </button>
              </>
            )}
            {!incomingCall && (
              <div className={`${styles.callActionBtn} ${styles.endCallBtn}`}>
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.1-.7-.28-.79-.73-1.68-1.36-2.66-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/></svg>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.callContainer} onClick={skipToNext}>
      <div className={styles.connectedScreen}>
        <div className={styles.connectedHeader}>
          <div className={styles.connectedAvatar}>
            {contactAvatar ? (
              <img src={contactAvatar} alt={contactName} />
            ) : (
              contactInitial
            )}
          </div>
          <div className={styles.connectedName}>{contactName}</div>
          <div className={styles.connectedDuration}>
            {phase === 'ended' ? 'Appel terminé' : formatDuration(callDuration)}
          </div>
        </div>

        <div className={styles.dialogueArea}>
          {displayedLines.map((dl, index) => (
            <div
              key={index}
              className={`${styles.dialogueLine} ${
                dl.message.sender === 'contact' ? styles.contactLine : styles.playerLine
              }`}
            >
              <span className={styles.lineSpeaker}>
                {dl.message.sender === 'contact' ? contactName : 'Vous'}
              </span>
              <p className={styles.lineText}>{dl.message.text}</p>
            </div>
          ))}

          {pendingReplyOptions && (
            <div className={styles.replyOptions} onClick={(e) => e.stopPropagation()}>
              <p className={styles.replyOptionsLabel}>Répondre</p>
              {pendingReplyOptions.map((option, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={styles.replyOptionButton}
                  onClick={() => handleReplyOptionClick(option)}
                >
                  {option.text}
                </button>
              ))}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {awaitingHangup && (
          <div className={styles.callActions} onClick={(e) => e.stopPropagation()}>
            <button type="button" className={styles.hangupButton} onClick={handleHangupClick}>
              Raccrocher
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
